import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Menu, Dropdown, Avatar, Button, message, Modal, Form, Input, Drawer, Tabs, Tooltip, notification } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  LogoutOutlined,
  GlobalOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  NotificationOutlined,
  HomeOutlined,
  GiftOutlined,
  EnvironmentOutlined,
  SettingOutlined,
  MailOutlined,
  LoginOutlined,
  PictureOutlined,
  AuditOutlined,
  MenuOutlined,
  DesktopOutlined,
  BarChartOutlined,
  CloudDownloadOutlined,
  ReloadOutlined,
  MoreOutlined,
  AccountBookOutlined,
  TagsOutlined,
  GoldOutlined,
  PhoneOutlined,
  DeleteOutlined,
  ApiOutlined,
  IdcardOutlined,
  RocketOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { Routes, Route, useNavigate, useLocation, matchPath } from 'react-router-dom';
import authService from '../utils/auth';
import Footer from '../components/Footer';
import axios from 'axios';
import useVersionCheck from '../hooks/useVersionCheck';
import './AdminLayout.css';

// Page components
import TourManagement from '../pages/TourManagement';
import PassportReminderSettings from '../pages/PassportReminderSettings';
import SalespersonManagement from '../pages/SalespersonManagement';
import AdminTourists from '../pages/AdminTourists';
import OCRLogs from '../pages/OCRLogs';
import NoticeTemplates from '../pages/NoticeTemplates';
import NoticeAttachments from '../pages/NoticeAttachments';
import TourNoticeEdit from '../pages/TourNoticeEdit';
import TourNoticePreview from '../pages/TourNoticePreview';
import DestinationManagement from '../pages/DestinationManagement';
import ResourceCenter from '../pages/ResourceCenter';
import HotelManagement from '../pages/HotelManagement';
import AirlineManagement from '../pages/AirlineManagement';
import FlightBatchManagement from '../pages/FlightBatchManagement';

import AddOnProductManagement from '../pages/AddOnProductManagement';
import AddOnNoticeTemplateEdit from '../pages/AddOnNoticeTemplateEdit';
import LoginLogManagement from '../pages/LoginLogManagement';
import OperationLogManagement from '../pages/OperationLogManagement';
import SessionManagement from '../pages/SessionManagement';
import Statistics from '../pages/Statistics';
import DataSync from '../pages/DataSync';
import NoticeWorkbench from '../pages/NoticeWorkbench';
import BillingStatistics from '../pages/BillingStatistics';
import InvoiceRecords from '../pages/InvoiceRecords';
import StoreManagement from '../pages/StoreManagement';
import PdfTextManager from '../pages/PdfTextManager';
import GroupTicketItinerary from '../pages/GroupTicketItinerary';
import VoucherManagement from '../pages/VoucherManagement';
import VoucherTemplateEdit from '../pages/VoucherTemplateEdit';
import TourTrash from '../pages/TourTrash';
import AiUsagePage from '../pages/AiUsagePage';
import PassportUpdateLogs from '../pages/PassportUpdateLogs';
// ProductCatalog 和 ProductEditor 已整合到 DestinationManagement 中

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const { Header, Sider, Content } = Layout;

// Route configuration: path pattern -> label + component
const ROUTE_CONFIG = [
  { path: '/admin/tours', label: '出团管理', component: TourManagement },
  { path: '/admin/salespersons', label: '员工管理', component: SalespersonManagement },
  { path: '/admin/tourists', label: '游客管理', component: AdminTourists },
  { path: '/admin/resource-center', label: '资源中心', component: ResourceCenter },
  { path: '/admin/destinations', label: '产品管理', component: DestinationManagement },
  { path: '/admin/hotels', label: '酒店库管理', component: HotelManagement },
  { path: '/admin/airlines', label: '航司管理', component: AirlineManagement },
  { path: '/admin/flight-batches', label: '航线库', component: FlightBatchManagement },

  { path: '/admin/addon-products', label: 'Add-ons产品', component: AddOnProductManagement },
  { path: '/admin/addon-products/:productId/notice-template', label: '通知书模板编辑', component: AddOnNoticeTemplateEdit },
  { path: '/admin/notice-templates', label: '通知模板', component: NoticeTemplates },
  { path: '/admin/notice-attachments', label: '附件管理', component: NoticeAttachments },
  { path: '/admin/ocr-logs', label: 'OCR识别日志', component: OCRLogs },
  { path: '/admin/login-logs', label: '登录记录', component: LoginLogManagement },
  { path: '/admin/operation-logs', label: '操作日志', component: OperationLogManagement },
  { path: '/admin/sessions', label: '会话管理', component: SessionManagement },
  { path: '/admin/data-sync', label: '数据同步', component: DataSync },
  { path: '/admin/statistics', label: '数据统计', component: Statistics },
  { path: '/admin/billing', label: '账单统计', component: BillingStatistics },
  { path: '/admin/invoices', label: 'Invoice', component: InvoiceRecords },
  { path: '/admin/stores', label: '门店管理', component: StoreManagement },
  { path: '/admin/pdf-text', label: 'PDF文本管理', component: PdfTextManager },
  { path: '/admin/group-ticket-itinerary', label: '团票行程单', component: GroupTicketItinerary },
  { path: '/admin/tours/:tourId/notice', label: '出团通知书', component: TourNoticeEdit },
  { path: '/admin/tours/:tourId/notice-preview', label: '通知书预览', component: TourNoticePreview },
  { path: '/admin/tours/:tourId/notice-workbench', label: '通知书工作台', component: NoticeWorkbench },
  { path: '/admin/vouchers', label: 'Voucher 列表', component: VoucherManagement },
  { path: '/admin/vouchers/template', label: 'Voucher 格式', component: VoucherTemplateEdit },
  { path: '/admin/tours/trash', label: '团回收站', component: TourTrash },
  { path: '/admin/ai-usage', label: 'AI Token 用量', component: AiUsagePage },
  { path: '/admin/passport-update-logs', label: '护照更新记录', component: PassportUpdateLogs },
  { path: '/admin/reminder-settings', label: '护照提醒设置', component: PassportReminderSettings },
];

// Find matching route config for a pathname
const findRouteConfig = (pathname) => {
  for (const config of ROUTE_CONFIG) {
    if (matchPath(config.path, pathname)) return config;
  }
  return null;
};

function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = authService.getUserInfo();
  const [form] = Form.useForm();

  // 版本检测：发现新版本弹出刷新提示
  const handleNewVersion = useCallback((newVer) => {
    notification.info({
      key: 'version-update',
      message: '发现新版本',
      description: `系统已更新至 v${newVer}，请刷新页面以获取最新功能。`,
      duration: 0,
      btn: (
        <Button type="primary" size="small" onClick={() => window.location.reload()}>
          立即刷新
        </Button>
      ),
    });
  }, []);
  useVersionCheck(handleNewVersion);

  // Tab state - restore from localStorage, ensure current URL is included
  const [tabs, setTabs] = useState(() => {
    let currentPath = location.pathname;
    if (currentPath === '/admin' || currentPath === '/admin/') currentPath = userInfo?.role === 'finance' ? '/admin/billing' : '/admin/tours';

    // Restore saved tabs
    let savedTabs = [];
    try {
      const stored = localStorage.getItem('admin_tabs');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate each saved tab still has a valid route config
        savedTabs = parsed.filter(t => t.key && t.label && findRouteConfig(t.key));
      }
    } catch {}

    // Ensure current URL has a tab
    const currentConfig = findRouteConfig(currentPath);
    if (currentConfig && !savedTabs.some(t => t.key === currentPath)) {
      savedTabs.push({ key: currentPath, label: currentConfig.label, path: currentConfig.path });
    }

    return savedTabs.length > 0 ? savedTabs : [];
  });
  const [activeKey, setActiveKey] = useState(() => {
    let p = location.pathname;
    if (p === '/admin' || p === '/admin/') p = userInfo?.role === 'finance' ? '/admin/billing' : '/admin/tours';
    return p;
  });
  const [refreshKeys, setRefreshKeys] = useState({});
  // Track which tabs have been visited (lazy mount: only render component after first activation)
  const [visitedKeys, setVisitedKeys] = useState(() => new Set([
    (location.pathname === '/admin' || location.pathname === '/admin/') ? '/admin/tours' : location.pathname
  ]));

  // Persist tabs to localStorage whenever they change
  useEffect(() => {
    if (tabs.length > 0) {
      localStorage.setItem('admin_tabs', JSON.stringify(tabs));
    }
  }, [tabs]);

  // Watch route changes -> create/activate tabs
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/admin' || currentPath === '/admin/') {
      const defaultPath = userInfo?.role === 'finance' ? '/admin/billing' : '/admin/tours';
      navigate(defaultPath, { replace: true });
      return;
    }
    const config = findRouteConfig(currentPath);
    if (!config) return;

    setTabs(prev => {
      if (prev.some(t => t.key === currentPath)) return prev;
      return [...prev, { key: currentPath, label: config.label, path: config.path }];
    });
    setActiveKey(currentPath);
    setVisitedKeys(prev => {
      if (prev.has(currentPath)) return prev;
      const next = new Set(prev);
      next.add(currentPath);
      return next;
    });
  }, [location.pathname, navigate]);

  // Watch window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setDrawerVisible(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      message.success('退出成功');
      navigate('/login');
    } catch (error) {
      message.error('退出失败');
    }
  };

  const handleChangePassword = async (values) => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/change-password`, {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword
      }, {
        headers: authService.getAuthHeaders()
      });

      message.success('密码修改成功');
      form.resetFields();
      setChangePasswordVisible(false);
    } catch (error) {
      message.error(error.response?.data?.error || '密码修改失败');
    } finally {
      setLoading(false);
    }
  };

  // Tab handlers
  const handleTabChange = (key) => {
    setActiveKey(key);
    navigate(key);
  };

  const handleTabEdit = (targetKey, action) => {
    if (action !== 'remove') return;
    if (tabs.length <= 1) return;

    const index = tabs.findIndex(t => t.key === targetKey);
    const newTabs = tabs.filter(t => t.key !== targetKey);

    if (targetKey === activeKey) {
      const newActive = newTabs[Math.min(index, newTabs.length - 1)];
      setTabs(newTabs);
      setActiveKey(newActive.key);
      navigate(newActive.key);
    } else {
      setTabs(newTabs);
    }
  };

  const handleRefresh = () => {
    setRefreshKeys(prev => ({ ...prev, [activeKey]: Date.now() }));
  };

  const handleCloseOtherTabs = () => {
    const current = tabs.find(t => t.key === activeKey);
    if (current) {
      setTabs([current]);
    }
  };

  const handleCloseAllTabs = () => {
    const defaultPath = '/admin/tours';
    const defaultConfig = findRouteConfig(defaultPath);
    const defaultTab = { key: defaultPath, label: defaultConfig?.label || '出团管理', path: defaultPath };
    setTabs([defaultTab]);
    setActiveKey(defaultPath);
    navigate(defaultPath);
  };

  // Render component for a tab via Routes with custom location
  const renderTabContent = (tab) => {
    return (
      <Routes location={tab.key}>
        {ROUTE_CONFIG.map(config => {
          const Component = config.component;
          return (
            <Route
              key={config.path}
              path={config.path.replace('/admin', '')}
              element={<Component />}
            />
          );
        })}
      </Routes>
    );
  };

  // Build menu items based on permissions
  const permissions = authService.getPermissions();

  const buildMenuItems = () => {
    const items = [];

    if (permissions.includes('tours')) {
      items.push({
        key: '/admin/tours',
        icon: <GlobalOutlined />,
        label: '出团管理',
      });
    }

    if (permissions.includes('salespersons')) {
      items.push({
        key: '/admin/salespersons',
        icon: <TeamOutlined />,
        label: '员工管理',
      });
    }

    if (permissions.includes('tourists')) {
      items.push({
        key: '/admin/tourists',
        icon: <UserOutlined />,
        label: '游客管理',
      });
    }

    if (permissions.includes('notice')) {
      items.push({
        key: 'notice-submenu',
        icon: <NotificationOutlined />,
        label: '资源中心',
        children: [
          {
            key: '/admin/resource-center',
            icon: <AppstoreOutlined />,
            label: '资源总览',
          },
          {
            key: '/admin/destinations',
            icon: <EnvironmentOutlined />,
            label: '产品管理',
          },
          {
            key: '/admin/hotels',
            icon: <HomeOutlined />,
            label: '酒店库管理',
          },
          {
            key: '/admin/airlines',
            icon: <RocketOutlined />,
            label: '航司管理',
          },
          {
            key: '/admin/flight-batches',
            icon: <RocketOutlined />,
            label: '航线库',
          },
          {
            key: '/admin/addon-products',
            icon: <GiftOutlined />,
            label: 'Add-ons产品',
          },
          {
            key: '/admin/notice-attachments',
            icon: <PictureOutlined />,
            label: '附件管理',
          },
          {
            key: '/admin/pdf-text',
            icon: <FileTextOutlined />,
            label: 'PDF文本管理',
          },
        ],
      });
    }

    // 团票行程单：出票环节由票务/管理员操作，销售不涉及，所以按 role 判定而非模块权限
    if (userInfo?.role === 'admin') {
      const groupTicketItem = {
        key: '/admin/group-ticket-itinerary',
        icon: <FilePdfOutlined />,
        label: '团票行程单',
      };
      const resourceMenu = items.find((it) => it.key === 'notice-submenu');
      if (resourceMenu) resourceMenu.children.push(groupTicketItem);
      else items.push(groupTicketItem);
    }

    // 数据统计菜单
    if (permissions.includes('statistics')) {
      items.push({
        key: '/admin/statistics',
        icon: <BarChartOutlined />,
        label: '数据统计',
      });
    }

    // 财务管理菜单
    if (permissions.includes('finance')) {
      items.push({
        key: 'finance-submenu',
        icon: <AccountBookOutlined />,
        label: '财务管理',
        children: [
          {
            key: '/admin/billing',
            icon: <BarChartOutlined />,
            label: '账单统计',
          },
        ],
      });
    }

    // Voucher 管理（一级菜单，admin/finance/operations 可见）
    if (permissions.includes('voucher')) {
      items.push({
        key: 'voucher-submenu',
        icon: <GoldOutlined />,
        label: 'Voucher 管理',
        children: [
          {
            key: '/admin/vouchers',
            icon: <TagsOutlined />,
            label: 'Voucher 列表',
          },
          {
            key: '/admin/vouchers/template',
            icon: <FileTextOutlined />,
            label: 'Voucher 格式',
          },
        ],
      });
    }

    // Invoice 记录菜单（顶层 - 所有角色可见）
    items.push({
      key: '/admin/invoices',
      icon: <FileTextOutlined />,
      label: 'Invoice',
    });

    // 系统设置菜单（放在最后）—— admin 或 operations 可见
    // ocr-logs 权限主要给 admin（OCR识别/登录/操作日志等系统级日志），
    // operations 主要看团回收站 + 护照更新记录，所以放宽外层条件
    if (permissions.includes('ocr-logs') || userInfo?.role === 'admin' || userInfo?.role === 'operations') {
      const systemChildren = [];

      // OCR日志、AI Token 用量、登录记录、操作日志仅管理员可见
      if (userInfo?.role === 'admin') {
        systemChildren.push(
          {
            key: '/admin/ocr-logs',
            icon: <FileTextOutlined />,
            label: 'OCR识别日志',
          },
          {
            key: '/admin/ai-usage',
            icon: <ApiOutlined />,
            label: 'AI Token 用量',
          },
          {
            key: '/admin/login-logs',
            icon: <LoginOutlined />,
            label: '登录记录',
          },
          {
            key: '/admin/operation-logs',
            icon: <AuditOutlined />,
            label: '操作日志',
          },
        );
      }

      if (userInfo?.role === 'admin') {
        systemChildren.push({
          key: '/admin/stores',
          icon: <HomeOutlined />,
          label: '门店管理',
        });
      }

      // 会话管理只有管理员可见
      if (userInfo?.role === 'admin') {
        systemChildren.push({
          key: '/admin/sessions',
          icon: <DesktopOutlined />,
          label: '会话管理',
        });
        systemChildren.push({
          key: '/admin/data-sync',
          icon: <CloudDownloadOutlined />,
          label: '数据同步',
        });
        systemChildren.push({
          key: '/admin/reminder-settings',
          icon: <MailOutlined />,
          label: '护照提醒设置',
        });
      }

      // 团回收站（admin/operations 可见）
      systemChildren.push({
        key: '/admin/tours/trash',
        icon: <DeleteOutlined />,
        label: '团回收站',
      });

      // 护照更新记录（admin + operations 可见）
      if (userInfo?.role === 'admin' || userInfo?.role === 'operations') {
        systemChildren.push({
          key: '/admin/passport-update-logs',
          icon: <IdcardOutlined />,
          label: '护照更新记录',
        });
      }

      items.push({
        key: 'system-submenu',
        icon: <SettingOutlined />,
        label: '系统设置',
        children: systemChildren,
      });
    }

    return items;
  };

  const menuItems = buildMenuItems();

  const userMenuItems = [
    {
      key: 'change-password',
      icon: <UserOutlined />,
      label: '修改密码',
    },
    // 后台人员自己带客人时用：销售端才有「代客上传护照」的识别流程。
    //
    // 对【所有】admin/operations 都显示，不按「名下有没有客人」动态判断（用户 2026-08-28 定）：
    // 这些人平时不带客人，但 TTOUR 订单随时可能按 broker 挂到他名下（Stephen 接到 1 单、
    // Ceci 名下 32 位未出发客人里 9 位待传证件，两人此前都因为进不了销售端而卡住）。
    // 动态判断会让「刚接到单的人」在下次登录前看不到入口，正是这次事故的成因；
    // 而没客人的账号点进去只是个空列表，代价小得多。
    ...(['admin', 'operations'].includes(userInfo?.role) ? [{
      // 落到出境游列表（销售端默认页），不是「我的游客」——切过去先看团、再进团管客人
      key: '/sales/tours?tab=standard',
      icon: <TeamOutlined />,
      label: '销售角色',
    }] : []),
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === 'change-password') {
      setChangePasswordVisible(true);
    } else if (key !== 'logout') {
      navigate(key);
      // 移动端点击菜单后关闭抽屉
      if (isMobile) {
        setDrawerVisible(false);
      }
    }
  };

  // 菜单内容组件
  const menuContent = (
    <>
      <div className="admin-logo">
        <h2>{isMobile ? '出团管理' : (collapsed ? 'OT' : '出团管理系统')}</h2>
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
      />
    </>
  );

  return (
    <Layout className={`admin-layout ${isMobile ? 'mobile' : ''}`}>
      {/* 桌面端侧边栏 */}
      {!isMobile && (
        <Sider trigger={null} collapsible collapsed={collapsed} className="admin-sider">
          {menuContent}
        </Sider>
      )}

      {/* 移动端抽屉菜单 */}
      {isMobile && (
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={250}
          bodyStyle={{ padding: 0, background: '#001529' }}
          className="mobile-drawer"
        >
          {menuContent}
        </Drawer>
      )}

      <Layout>
        <Header className={`admin-header ${collapsed && !isMobile ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}>
          <div className="header-left">
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{ fontSize: '16px', width: 48, height: 64, color: 'white' }}
              />
            ) : (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ fontSize: '16px', width: 64, height: 64, color: 'white' }}
              />
            )}
            <span className="page-title">{userInfo?.role === 'admin' ? '管理员后台' : userInfo?.role === 'finance' ? '财务后台' : '运营后台'}</span>
          </div>
          <div className="header-right">
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleMenuClick }}
              placement="bottomRight"
            >
              <div className="user-info">
                <Avatar icon={<UserOutlined />} size={isMobile ? 'small' : 'default'} />
                {!isMobile && <span className="user-name">{userInfo?.name}</span>}
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* Tab Bar */}
        <div className={`admin-tabs-bar ${collapsed && !isMobile ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}>
          <Tabs
            type="editable-card"
            hideAdd
            activeKey={activeKey}
            onChange={handleTabChange}
            onEdit={handleTabEdit}
            size="small"
            items={tabs.map(tab => ({
              key: tab.key,
              label: tab.label,
              closable: tabs.length > 1,
            }))}
            tabBarExtraContent={
              <Dropdown
                menu={{
                  items: [
                    { key: 'refresh', label: '刷新当前页', icon: <ReloadOutlined />, onClick: handleRefresh },
                    { type: 'divider' },
                    { key: 'closeOther', label: '关闭其他标签', disabled: tabs.length <= 1, onClick: handleCloseOtherTabs },
                    { key: 'closeAll', label: '关闭所有标签', danger: true, disabled: tabs.length <= 1, onClick: handleCloseAllTabs },
                  ]
                }}
                trigger={['click']}
              >
                <Button type="text" icon={<MoreOutlined />} size="small" style={{ marginRight: 12 }} />
              </Dropdown>
            }
          />
        </div>

        <Content className={`admin-content ${collapsed && !isMobile ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}>
          {tabs.map(tab => (
            <div key={tab.key} style={{ display: tab.key === activeKey ? 'block' : 'none' }}>
              {visitedKeys.has(tab.key) && (
                <div key={refreshKeys[tab.key] || tab.key}>
                  {renderTabContent(tab)}
                </div>
              )}
            </div>
          ))}
          <Footer className="layout-footer" />
        </Content>
      </Layout>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={changePasswordVisible}
        onCancel={() => {
          setChangePasswordVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            name="oldPassword"
            label="原密码"
            rules={[
              { required: true, message: '请输入原密码' }
            ]}
          >
            <Input.Password placeholder="请输入原密码" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度至少6位' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setChangePasswordVisible(false);
                  form.resetFields();
                }}
              >
                取消
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                确定修改
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default AdminLayout;
